import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonIcon } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import removeAccessorial from "@tKgpco/Handlers/Accessorials/RemoveAccessorial";
import selectAccessorials from "@tKgpco/Handlers/Accessorials/SelectAccessorials";
import selectAllAccessorials from "@tKgpco/Handlers/Accessorials/SelectAllAccessorials";
import setAccessorialSearchFilter from "@tKgpco/Handlers/Accessorials/SetAccessorialSearchFilter";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    removeAccessorial,
    selectAccessorials,
    selectAllAccessorials,
    setAccessorialSearchFilter
};

const mapStateToProps = (state: CustomApplicationState) => ({
    isLoading: state.KGPState.customAccessorials.isLoading,
    accessorials: state.KGPState.customAccessorials.accessorials,
    selectedAccessorialIds: state.KGPState.customAccessorials.selectedAccessorialIds,
    isAllAccessorialSelected: state.KGPState.customAccessorials.isAllAccessorialSelected,
});

interface AccessorialsTableStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    checkboxHeader?: DataTableHeaderProps;
    editHeader?: DataTableHeaderProps;
    nameHeader?: DataTableHeaderProps;
    codeHeader?: DataTableHeaderProps;
    enabledHeader?: DataTableHeaderProps;
    descriptionHeader?: DataTableHeaderProps;
    checkboxCells?: DataTableCellProps;
    editCells?: DataTableCellProps;
    nameCells?: DataTableCellProps;
    codeCells?: DataTableCellProps;
    enabledCells?: DataTableCellProps;
    descriptionCells?: DataTableCellProps;
    rowCheckbox?: CheckboxPresentationProps;
}

const styles: AccessorialsTableStyles = {
    wrapper: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    checkboxHeader: {
        tight: true,
        alignX: "center",
    },
    editHeader: {
        tight: true,
        alignX: "center",
    },
    enabledHeader: {
        tight: true,
        alignX: "left",
    },
    nameHeader: {
        tight: true,
        alignX: "left",
    },
    codeHeader: {
        tight: true,
        alignX: "left",
    },
    rowCheckbox: {
        css: css`
            border: 1px solid #a0a3ab;
        `
    },
    descriptionCells: {
        css: css`
            span {
                display: inline-block;      
            }
        `
    }
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const AccessorialsTable: React.FC<Props> = ({
    id,
    isLoading,
    accessorials,
    selectedAccessorialIds,
    isAllAccessorialSelected,
    removeAccessorial,
    selectAccessorials,
    selectAllAccessorials,
    setAccessorialSearchFilter
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const checkboxChangeHandler = (value: boolean, id?: string) => {
        if (id) {
            selectAccessorials({ ids: [id] });
        } else {
            selectAllAccessorials({ isAllAccessorialSelected: value});
        }        
    };

    const handleDelete = (id: string) => {
        removeAccessorial({
            id,
            onSuccess: () => {
                setIsSubmitting(false);
                setAccessorialSearchFilter({ key: "selectFilter", value: "" })
                setAccessorialSearchFilter({ key: "selectFilterText", value: "" })
                toasterContext.addToast({
                    messageType: "success",
                    body: translate("Accessorial removed successfully."),
                    timeoutLength: 4000,
                });
            },
        });
    };

    useEffect(() => {
        let ids: string[] = [];
        if(isAllAccessorialSelected) {
            for (const row of accessorials!) {
                if (!selectedAccessorialIds[row.id]) {
                    ids.push(row.id);
                }
            }
        } else {
            ids = Object.keys(selectedAccessorialIds);
        }
        selectAccessorials({ ids });
    },[isAllAccessorialSelected, accessorials]);

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!accessorials) {
        return null;
    }

    if (accessorials.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography as="p" {...styles.noResultsText} data-test-selector="accessorialsTable_noOrdersFound">
                    {translate("No results found")}
                </Typography>
            </StyledWrapper>
        );
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            <DataTable>
                <DataTableHead>
                    <DataTableHeader {...styles.checkboxHeader}>
                        <Checkbox
                            {...styles.rowCheckbox}
                            checked={isAllAccessorialSelected}
                            onChange={(e, value) => checkboxChangeHandler(value)}
                        />
                    </DataTableHeader>
                    <DataTableHeader {...styles.editHeader}>{translate("Edit")}</DataTableHeader>
                    <DataTableHeader {...styles.nameHeader}>{translate("Master")}</DataTableHeader>
                    <DataTableHeader {...styles.nameHeader}>{translate("Name")}</DataTableHeader>
                    <DataTableHeader {...styles.nameHeader}>{translate("Type")}</DataTableHeader>
                    <DataTableHeader {...styles.codeHeader}>{translate("Code")}</DataTableHeader>
                    <DataTableHeader {...styles.enabledHeader}>{translate("Enabled")}</DataTableHeader>
                    <DataTableHeader {...styles.descriptionHeader}>{translate("Description")}</DataTableHeader>
                    <DataTableHeader {...styles.editHeader}>
                        <VisuallyHidden>{translate("Delete")}</VisuallyHidden>
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {accessorials.map(accessorial => (
                        <DataTableRow key={accessorial.id}>
                            <DataTableCell {...styles.checkboxCells}>
                                <Checkbox
                                    {...styles.rowCheckbox}
                                    checked={selectedAccessorialIds[accessorial.id]}
                                    onChange={(e, value) => checkboxChangeHandler(value, accessorial.id)}
                                />
                            </DataTableCell>
                            <DataTableCell {...styles.editCells}>
                                <Link href={`/MyAccount/Accessorials/EditAccessorials?accessorialId=${accessorial.id}`}>
                                    <Icon src="Edit2" size={20} />
                                </Link>
                            </DataTableCell>
                            <DataTableCell {...styles.codeCells}>{accessorial.master}</DataTableCell>
                            <DataTableCell {...styles.codeCells}>{accessorial.name}</DataTableCell>
                            <DataTableCell {...styles.codeCells}>{accessorial.type}</DataTableCell>
                            <DataTableCell {...styles.codeCells}>{accessorial.tI_Code}</DataTableCell>
                            <DataTableCell {...styles.enabledCells}>
                                {accessorial.enabled ? "true" : "false"}
                            </DataTableCell>
                            <DataTableCell {...styles.descriptionCells}>{accessorial.tI_Description}</DataTableCell>
                            <DataTableCell {...styles.editCells}>
                                <Clickable onClick={() => handleDelete(accessorial.id)}>
                                    <Icon src="Trash2" size={20} />
                                </Clickable>
                            </DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const accessorialsTable: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccessorialsTable),
    definition: {
        group: "Basic",
    },
};

export default accessorialsTable;
